/* DupeOutdoors -- custom interactions */
'use strict';

/* =====================================================
   Mobile nav drawer
   ===================================================== */
class DupeNavDrawer {
  constructor() {
    this.drawer  = document.getElementById('do-nav-drawer');
    this.trigger = document.getElementById('do-hamburger');
    this.closeEl = document.getElementById('do-drawer-close');
    this.overlay = this.drawer && this.drawer.querySelector('.do-nav-drawer__overlay');
    if (!this.drawer || !this.trigger) return;

    this.trigger.addEventListener('click', () => this.open());
    this.closeEl && this.closeEl.addEventListener('click', () => this.close());
    this.overlay && this.overlay.addEventListener('click', () => this.close());
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) this.close();
    });
  }

  open() {
    this.drawer.classList.add('is-open');
    this.trigger.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => this.closeEl && this.closeEl.focus());
  }

  close() {
    this.drawer.classList.remove('is-open');
    this.trigger.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    this.trigger.focus();
  }
}

/* =====================================================
   PDP gallery -- thumbnail switcher
   ===================================================== */
class DupeGallery {
  constructor(el) {
    this.el     = el;
    this.mainEl = el.querySelector('[data-gallery-main]');
    this.thumbs = el.querySelectorAll('[data-gallery-thumb]');
    el._gallery = this; // expose instance for variant picker lookup
    this.thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => this.select(i));
      thumb.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.select(i); }
      });
    });
  }

  select(i) {
    const thumb = this.thumbs[i];
    if (!thumb || !this.mainEl) return;
    const src    = thumb.dataset.src;
    const srcset = thumb.dataset.srcset;
    const alt    = thumb.dataset.alt || '';
    const imgEl  = this.mainEl.querySelector('img');
    if (imgEl && src) {
      imgEl.src = src;
      if (srcset) imgEl.srcset = srcset;
      imgEl.alt = alt;
      imgEl.style.display = '';
    }
    const noPhoto = this.mainEl.querySelector('.do-gallery__no-photo');
    if (noPhoto) noPhoto.style.display = 'none';
    this.thumbs.forEach(t => t.classList.remove('is-active'));
    thumb.classList.add('is-active');
  }

  selectByPosition(pos) {
    this.select(pos - 1); // Shopify positions are 1-indexed
  }
}

/* =====================================================
   Quantity stepper
   ===================================================== */
function initQuantitySteppers() {
  document.querySelectorAll('.do-qty').forEach((wrap) => {
    const input = wrap.querySelector('.do-qty__input');
    if (!input) return;
    wrap.querySelector('[data-do-dec]') &&
      wrap.querySelector('[data-do-dec]').addEventListener('click', () => {
        const v = parseInt(input.value, 10);
        if (v > (parseInt(input.min, 10) || 1)) {
          input.value = v - 1;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    wrap.querySelector('[data-do-inc]') &&
      wrap.querySelector('[data-do-inc]').addEventListener('click', () => {
        const v = parseInt(input.value, 10);
        const max = input.max ? parseInt(input.max, 10) : Infinity;
        if (v < max) {
          input.value = v + 1;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
  });
}

/* =====================================================
   Smooth anchor scroll
   ===================================================== */
function initSmoothAnchors() {
  document.querySelectorAll('a[href^="#"]').forEach(link => {
    link.addEventListener('click', (e) => {
      const id = link.getAttribute('href').slice(1);
      const target = document.getElementById(id);
      if (!target) return;
      e.preventDefault();
      const start = window.scrollY;
      const end   = target.getBoundingClientRect().top + window.scrollY - 80;
      const dist  = end - start;
      const dur   = Math.min(Math.abs(dist) * 0.5, 900);
      const startTime = performance.now();
      const ease = t => t < 0.5 ? 4*t*t*t : 1 - Math.pow(-2*t+2,3)/2;
      const step = (now) => {
        const p = Math.min((now - startTime) / dur, 1);
        window.scrollTo(0, start + dist * ease(p));
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    });
  });
}

/* =====================================================
   Scroll reveal -- IntersectionObserver
   ===================================================== */
function initScrollReveal() {
  const els = document.querySelectorAll('[data-reveal]');
  if (!els.length) return;
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
  els.forEach(el => observer.observe(el));
}

/* =====================================================
   Count-up animation for hero price cards
   ===================================================== */
function initCountUp() {
  const targets = document.querySelectorAll('[data-count-up]');
  if (!targets.length) return;
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      const el  = entry.target;
      const end = parseInt(el.dataset.countUp, 10);
      const dur = 1100;
      const start = performance.now();
      const tick  = (now) => {
        const p = Math.min((now - start) / dur, 1);
        const eased = 1 - Math.pow(1 - p, 3);
        el.textContent = Math.round(eased * end);
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
      observer.unobserve(el);
    });
  }, { threshold: 0.5 });
  targets.forEach(el => observer.observe(el));
}

/* =====================================================
   Variant picker -- PDP
   ===================================================== */
function formatMoney(cents) {
  return '$' + (cents / 100).toFixed(2);
}

class DupeVariantPicker {
  constructor(wrap) {
    this.wrap = wrap;
    this.form = wrap.querySelector('form[data-type="add-to-cart-form"]');
    if (!this.form) return;

    const variantsEl = this.form.querySelector('script[type="application/json"]');
    if (!variantsEl) return;

    try { this.variants = JSON.parse(variantsEl.textContent); }
    catch (e) { return; }

    this.idInput     = this.form.querySelector('input[name="id"]');
    this.priceEl     = wrap.querySelector('[data-brand-price]');
    this.brandPrice  = parseInt((this.priceEl && this.priceEl.dataset.brandPrice) || '0', 10);
    this.atcBtn      = this.form.querySelector('[name="add"]');
    this.galleryMain = wrap.querySelector('[data-gallery-main]');
    const galleryEl  = wrap.querySelector('[data-do-gallery]');
    this.gallery     = (galleryEl && galleryEl._gallery) || null;

    // Seed selection from the initially rendered checked pills
    this.selected = {};
    wrap.querySelectorAll('.do-variant-pill.is-selected').forEach(btn => {
      this.selected[btn.dataset.optionPosition] = btn.dataset.optionValue;
    });

    // Attach click listeners to every pill (none have disabled attr now)
    wrap.querySelectorAll('.do-variant-pill').forEach(btn => {
      btn.addEventListener('click', () => this.onPillClick(btn));
    });
  }

  onPillClick(btn) {
    const position = btn.dataset.optionPosition;
    const value    = btn.dataset.optionValue;

    // Highlight selected pill in this group
    btn.closest('.do-variant-pills').querySelectorAll('.do-variant-pill').forEach(b => {
      b.classList.remove('is-selected');
      b.setAttribute('aria-checked', 'false');
    });
    btn.classList.add('is-selected');
    btn.setAttribute('aria-checked', 'true');

    // Update the "Color: Red" label text
    const group = btn.closest('.do-variant-group');
    if (group) {
      const labelEm = group.querySelector('em');
      if (labelEm) labelEm.textContent = value;
    }

    this.selected[position] = value;

    const variant = this.findVariant();
    if (!variant) return;

    if (this.idInput) this.idInput.value = variant.id;
    this.updatePrice(variant);
    this.updateAtc(variant);
    this.updateGallery(variant);
  }

  findVariant() {
    return this.variants.find(v =>
      Object.keys(this.selected).every(pos => v['option' + pos] === this.selected[pos])
    );
  }

  updatePrice(variant) {
    if (!this.priceEl) return;
    const ourPriceEl   = this.priceEl.querySelector('.receipt__our-price');
    const savingsEl    = this.priceEl.querySelector('.receipt__savings');
    const brandPriceEl = this.priceEl.querySelector('.receipt__brand-price');
    const brandRow     = this.priceEl.querySelector('.receipt__brand-row');

    const ourPrice   = variant.price;
    const brandPrice = this.brandPrice > 0 ? this.brandPrice : (variant.compare_at_price || 0);
    const savings    = brandPrice > ourPrice ? brandPrice - ourPrice : 0;

    if (ourPriceEl)   ourPriceEl.textContent = formatMoney(ourPrice);
    if (brandPriceEl && brandPrice > 0) brandPriceEl.textContent = formatMoney(brandPrice);
    if (brandRow)     brandRow.style.display = brandPrice > ourPrice ? '' : 'none';
    if (savingsEl) {
      if (savings > 0) {
        savingsEl.textContent   = 'SAVE ' + formatMoney(savings);
        savingsEl.style.display = '';
      } else {
        savingsEl.style.display = 'none';
      }
    }
  }

  updateAtc(variant) {
    if (!this.atcBtn) return;
    this.atcBtn.disabled    = !variant.available;
    this.atcBtn.textContent = variant.available ? 'Add to cart' : 'Sold out';
  }

  updateGallery(variant) {
    const fi = variant.featured_image;
    if (!fi || !fi.src) return;

    const fiBase = fi.src.split('?')[0];

    // If a thumbnail strip exists, find the thumb whose URL matches this
    // variant's image and activate it (handles any number of thumbs)
    if (this.gallery && this.gallery.thumbs.length > 0) {
      const thumbs   = Array.from(this.gallery.thumbs);
      const matchIdx = thumbs.findIndex(t => {
        const tSrc = (t.dataset.src || '').split('?')[0];
        return tSrc && tSrc === fiBase;
      });
      if (matchIdx >= 0) {
        this.gallery.select(matchIdx);
        return;
      }
    }

    // No matching thumbnail (variant image outside the strip cap, or no strip)
    // — swap the main image directly
    if (!this.galleryMain) return;
    const src     = fiBase + '?width=900';
    const noPhoto = this.galleryMain.querySelector('.do-gallery__no-photo');
    let img       = this.galleryMain.querySelector('img');

    if (img) {
      img.src           = src;
      img.alt           = fi.alt || '';
      img.style.display = '';
    } else {
      img             = document.createElement('img');
      img.src         = src;
      img.alt         = fi.alt || '';
      img.width       = 900;
      img.height      = 900;
      img.style.width = '100%';
      this.galleryMain.insertBefore(img, noPhoto || null);
    }
    if (noPhoto) noPhoto.style.display = 'none';
  }
}

/* =====================================================
   Boot
   ===================================================== */
document.addEventListener('DOMContentLoaded', () => {
  new DupeNavDrawer();
  // Gallery must init before variant picker so el._gallery is set
  document.querySelectorAll('[data-do-gallery]').forEach(el => new DupeGallery(el));
  // Variant picker reads el._gallery from the already-initialized gallery
  document.querySelectorAll('.do-pdp-wrap').forEach(wrap => new DupeVariantPicker(wrap));
  initQuantitySteppers();
  initSmoothAnchors();
  initScrollReveal();
  initCountUp();
});
